// web/src/routes/dashboard/GroomDashboard.jsx
// Answers: "What do I do next?"
// MOBILE-FIRST.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtDate, todayLong } from '../../lib/dates';

export default function GroomDashboard() {
  const [timeBlock, setTimeBlock] = useState(currentTimeBlock());
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'groom', timeBlock],
    queryFn: () => api.get(`/dashboard/groom?block=${timeBlock}`),
  });

  const completeTask = useMutation({
    mutationFn: (taskId) => api.post(`/tasks/${taskId}/complete`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'groom'] }),
  });

  const tasks = data?.tasks ?? [];
  const remaining = tasks.filter((t) => t.status === 'pending').length;

  return (
    <div className="pb-24">
      {/* The planner's ruling and hunter ink, but NOT its density. This is the
          one dashboard used standing in an aisle with gloves on, and the
          48-pixel tick is the whole interface — shrinking it to ruled rows to
          match the other four would be matching for its own sake. The header
          stays sticky for the same reason: the count is what you glance at
          between stalls. */}
      <header className="sticky top-0 z-10 bg-[#f5f8f4]/95 backdrop-blur border-b-2 border-hunter/30 px-4 py-3">
        <div className="flex items-end justify-between gap-3">
          <div className="flex items-end gap-3">
            <span className="text-3xl font-semibold leading-none text-hunter tabular-nums">
              {data?.today ? Number(data.today.slice(8, 10)) : '—'}
            </span>
            <span className="pb-0.5">
              <h1 className="text-lg font-semibold leading-tight">
                {remaining > 0 ? `${remaining} to go` : 'All done'}
              </h1>
              <time className="text-xs text-ink-500">
                {data?.today ? fmtDate(data.today, 'dayMedium') : todayLong()}
              </time>
            </span>
          </div>
          <time className="text-xs text-ink-400 pb-1">{formatNow(data?.timezone)}</time>
        </div>
        <TimeBlockTabs current={timeBlock} onChange={setTimeBlock} />
      </header>

      <div className="px-4 pt-4 space-y-2">
        {isLoading ? (
          <SkeletonTasks />
        ) : tasks.length === 0 ? (
          <EmptyState block={timeBlock} />
        ) : (
          tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onComplete={() => completeTask.mutate(task.id)}
              isCompleting={completeTask.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TimeBlockTabs({ current, onChange }) {
  const blocks = [
    { id: 'am',   label: 'AM' },
    { id: 'noon', label: 'Noon' },
    { id: 'pm',   label: 'PM' },
    { id: 'nc',   label: 'NC' },
  ];
  return (
    <div className="flex gap-1 mt-3">
      {blocks.map((b) => (
        <button
          key={b.id}
          onClick={() => onChange(b.id)}
          className={
            'flex-1 py-2 rounded-md text-sm font-medium transition ' +
            (current === b.id
              ? 'bg-hunter text-white'
              : 'bg-white text-hunter border border-hunter/30')
          }
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}

function TaskRow({ task, onComplete, isCompleting }) {
  const done = task.status === 'done';
  return (
    <div
      className={
        'flex items-center gap-3 p-4 rounded-xl border transition ' +
        (done ? 'bg-hunter/5 border-hunter/20 opacity-60' : 'bg-white border-hunter/20')
      }
    >
      <button
        onClick={onComplete}
        disabled={done || isCompleting}
        className={
          'w-12 h-12 rounded-full border-2 flex items-center justify-center transition shrink-0 ' +
          (done
            ? 'bg-hunter border-hunter text-white'
            : 'border-hunter/30 hover:border-hunter active:scale-95')
        }
        aria-label={done ? 'Completed' : 'Mark complete'}
      >
        {done ? '✓' : ''}
      </button>

      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {task.horseName ?? 'Barn-wide'}
          {task.stall && <span className="text-ink-400 text-sm ml-1">· stall {task.stall}</span>}
        </div>
        <div className="text-sm text-ink-500">
          {labelForTask(task)}
        </div>
      </div>

      {task.horseId && (
        <Link to={`/horses/${task.horseId}`} className="text-ink-300 px-2" aria-label="Details">
          ›
        </Link>
      )}
    </div>
  );
}

function EmptyState({ block }) {
  return (
    <div className="text-center py-12">
      <div className="text-5xl mb-3">🐴</div>
      <p className="text-ink-500">Nothing scheduled for {labelForBlock(block)}.</p>
    </div>
  );
}

function SkeletonTasks() {
  return (
    <>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-20 rounded-xl bg-white border border-ink-100 animate-pulse" />
      ))}
    </>
  );
}

function currentTimeBlock() {
  const h = new Date().getHours();
  if (h < 11) return 'am';
  if (h < 14) return 'noon';
  if (h < 20) return 'pm';
  return 'nc';
}

function labelForBlock(block) {
  return { am: 'AM', noon: 'noon', pm: 'PM', nc: 'night check' }[block] ?? block;
}

function labelForTask(task) {
  const parts = [task.taskType];
  if (task.rideType) parts.push(task.rideType);
  if (task.assignedName) parts.push(`· ${task.assignedName}`);
  return parts.join(' ');
}

function formatNow(timezone) {
  return new Date().toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
    timeZone: timezone || undefined,
  });
}
